import type { UseInfiniteQueryResult } from '@tanstack/react-query'
import { useState } from 'react'
import { useDebouncedCallback } from 'use-debounce'
import { Dropdown } from '~/components/Form'
import type {
  DropdownOption,
  SingleDropdownProps
} from '~/components/Form/Input/Dropdown'
import { useOffersAutocomplete } from '~/domains/offers/queries/useOffersAutocomplete'
import {
  dropdownWrapper,
  optionLabel,
  optionSubtitle,
  optionTitle
} from './OffersAutocomplete.css'

export const OffersAutocomplete = ({
  userIds,
  ids,
  agencyId,
  ...props
}: {
  userIds: number[]
  ids?: string[]
  agencyId?: number
} & Omit<SingleDropdownProps, 'optionsInfiniteQuery' | 'options'>) => {
  const [search, setSearch] = useState<string>()
  const { data, ...rest } = useOffersAutocomplete({
    userIds,
    ids:
      search !== undefined
        ? undefined
        : ids?.map((id) => Number.parseInt(id, 10)),
    search: search,
    agencyId
  })
  const options = data?.pages
    .flatMap((page) => page['hydra:member'])
    .map((offer) => ({
      label: offer.title,
      value: String(offer.id),
      estimateSize: 52,
      element: (
        <div className={optionLabel}>
          <div className={optionTitle}>{offer.title}</div>
          {(offer.reference || offer.order?.company?.name) && (
            <div className={optionSubtitle}>
              {[
                offer.reference,
                offer.order?.company?.name ?? offer.companyName
              ]
                .filter(Boolean)
                .join(' - ')}
            </div>
          )}
        </div>
      )
    }))

  const onSearchChange = useDebouncedCallback((search: string) => {
    setSearch(search)
  }, 300)

  return (
    <div className={dropdownWrapper}>
      <Dropdown
        optionsInfiniteQuery={
          {
            ...rest,
            data: options
          } as unknown as UseInfiniteQueryResult<DropdownOption[] | undefined>
        }
        defaultValue={ids?.[0]}
        onSearchChange={(search) => onSearchChange(search ?? '')}
        placeholder="Filtrer par offre"
        searchable
        searchShouldFilterOptions={false}
        {...props}
      />
    </div>
  )
}
